import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { ApiError, getAgents } from "./api";
import type { AgentStatus, AgentStaleness } from "./types";
import { colors } from "./theme";
import { formatDateTime, formatTime } from "./datetime";

// Fleet status — one row per helper that has heartbeated within the TTL
// window (30 days). Auto-refreshes every 30s while the tab is mounted;
// staleness comes from the server so we don't have to chase clock skew
// here.
export default function AgentsPage() {
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await getAgents();
      setAgents(sortAgents(res.agents));
      setLastRefresh(new Date());
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const online = agents.filter((a) => a.staleness === "online").length;
  const idle = agents.filter((a) => a.staleness === "idle").length;
  const offline = agents.filter((a) => a.staleness === "offline").length;
  const notInstalled = agents.filter((a) => a.staleness === "not-installed").length;
  // "installed" = anyone who has ever checked in (everything except not-installed).
  const expected = agents.filter((a) => a.inRoster).length;
  const installedExpected = agents.filter((a) => a.inRoster && a.staleness !== "not-installed").length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">Helper fleet</Typography>
        {expected > 0 && (
          <Chip
            label={`${installedExpected} / ${expected} installed`}
            size="small"
            color={installedExpected === expected ? "success" : "primary"}
          />
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Chip label={`${online} online`} size="small" color="success" variant="outlined" />
        <Chip label={`${idle} idle`} size="small" color="warning" variant="outlined" />
        <Chip label={`${offline} offline`} size="small" color="default" variant="outlined" />
        {notInstalled > 0 && (
          <Chip label={`${notInstalled} not installed`} size="small" color="error" variant="outlined" />
        )}
        <Tooltip title={lastRefresh ? `Last refresh: ${formatTime(lastRefresh)}` : "Refresh"}>
          <IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Table
        size="small"
        sx={{
          background: "white",
          border: `1px solid ${colors.gray200}`,
          borderRadius: 1,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Agent</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last seen</TableCell>
            <TableCell>Capture</TableCell>
            <TableCell>Hostname</TableCell>
            <TableCell>Finesse host</TableCell>
            <TableCell>Helper</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} sx={{ textAlign: "center", fontStyle: "italic", color: colors.gray500 }}>
                No agents to show yet. Installed helpers appear within ~5 minutes of first launch; seed the agent roster to also list agents who haven't installed.
              </TableCell>
            </TableRow>
          ) : (
            agents.map((a) => {
              const pending = a.staleness === "not-installed";
              return (
                <TableRow key={a.agentId} sx={pending ? { background: colors.gray50 } : undefined}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem", color: pending ? colors.gray500 : undefined }}>{a.agentId}</TableCell>
                  <TableCell sx={{ fontSize: "0.85rem" }}>{formatName(a) ?? <Box sx={{ color: colors.gray500 }}>—</Box>}</TableCell>
                  <TableCell><StalenessChip s={a.staleness} ageSeconds={a.ageSeconds} /></TableCell>
                  <TableCell>{formatLastSeen(a.lastSeen)}</TableCell>
                  <TableCell>
                    {a.captureStatus
                      ? <Box sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{a.captureStatus}</Box>
                      : <Box sx={{ color: colors.gray500 }}>—</Box>}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.85rem" }}>{a.hostname ?? "—"}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{a.activeFinesseHost ?? "—"}</TableCell>
                  <TableCell sx={{ fontSize: "0.85rem", color: colors.gray500 }}>{a.helperVersion ?? "—"}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

function StalenessChip({ s, ageSeconds }: { s: AgentStaleness; ageSeconds: number | null }) {
  if (s === "not-installed") {
    return <Chip label="not installed" size="small" color="error" variant="outlined" />;
  }
  const label = s === "unknown" ? "unknown" : `${s} · ${formatAge(ageSeconds)}`;
  const color = s === "online" ? "success" : s === "idle" ? "warning" : "default";
  return <Chip label={label} size="small" color={color} variant={s === "online" ? "filled" : "outlined"} />;
}

function formatName(a: AgentStatus): string | null {
  const name = [a.firstName, a.lastName].filter(Boolean).join(" ").trim();
  return name.length ? name : null;
}

function sortAgents(items: AgentStatus[]): AgentStatus[] {
  // Installed agents (by liveness) first; never-installed cluster at the bottom.
  const rank: Record<AgentStaleness, number> = {
    online: 0, idle: 1, offline: 2, unknown: 3, "not-installed": 4,
  };
  return [...items].sort((a, b) => {
    const r = rank[a.staleness] - rank[b.staleness];
    if (r !== 0) return r;
    return a.agentId.localeCompare(b.agentId);
  });
}

function formatLastSeen(iso: string | null): string {
  return formatDateTime(iso);
}

function formatAge(sec: number | null): string {
  if (sec == null) return "?";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}
